import {
  BoxIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ExpandIcon,
  LandPlotIcon,
  ListIcon,
  Minimize2Icon,
  ScrollTextIcon,
  TrashIcon,
  UserIcon,
} from "lucide-react";
import "./Debugger.v2.css";
import { Logo } from "../Logo";
import { DebuggerManager } from "@/stores/DebuggerManager";
import { observer } from "mobx-react-lite";
import { useState, useRef, useEffect } from "react";
import { animate, transition } from "@/lib/cssAnimation";
import { GuideListModal } from "./GuideListModal";
import { TourDetailsModal } from "./TourDetailsModal";
import { UserDataModal } from "./UserDataModal";
import React from "react";
import { StateDataModal } from "./StateDataModal";
import Highlighter from "./selector-picker/highlighter";
import ReactDOM from "react-dom";
import { getUrl } from "@/lib/window";
import { SelectorPickerBadge } from "./selector-picker/selector-picker-badge";
import { ChecklistListModal } from "./ChecklistListModal";
export interface DebuggerV2Props {
  manager: DebuggerManager;
}

export const DebuggerV2 = observer(({ manager }: DebuggerV2Props) => {
  const isMinimized = manager.isMinimized;
  const [showButtons, setShowButtons] = useState(!manager.isMinimized);
  const [clicks, setClicks] = useState(0);
  const [timer, setTimer] = useState<ReturnType<typeof setTimeout> | null>(null);

  const debuggerRef = useRef(null);
  const leftGroupRef = useRef(null);
  const userDataButtonRef = useRef(null);
  const clearStateButtonRef = useRef(null);
  const stateDataButtonRef = useRef(null);

  const handleMinimize = async () => {
    manager.closeAllModals();
    await Promise.all([
      animate(leftGroupRef.current, "fadeOut"),
      animate(userDataButtonRef.current, "fadeOut"),
      animate(clearStateButtonRef.current, "fadeOut"),
      animate(stateDataButtonRef.current, "fadeOut"),
    ]);
    setShowButtons(false);
    await transition(debuggerRef.current, { width: "106px" });
    manager.minimize();
  };

  const handleExpand = async () => {
    await transition(debuggerRef.current, { width: "100%" });
    setShowButtons(true);
    Promise.all([
      animate(leftGroupRef.current, "fadeIn"),
      animate(userDataButtonRef.current, "fadeIn"),
      animate(clearStateButtonRef.current, "fadeIn"),
      animate(stateDataButtonRef.current, "fadeIn"),
    ]);
    manager.expand();
  };

  const handlePick = (selector: { selector: string; rank: number }) => {
    const url = getUrl();
    manager.handleSelectorPicked({ ...selector, url });
  };

  useEffect(() => {
    if (clicks === 5) {
      manager.enableDevMode();
      setClicks(0);
      if (timer !== null) {
        clearTimeout(timer);
      }
    }
  }, [clicks, timer]);

  const handleLogoClick = () => {
    if (clicks === 0) {
      setTimer(setTimeout(() => setClicks(0), 2000));
    }
    setClicks((prev) => prev + 1);
  };

  useEffect(() => {
    if (showButtons) {
      requestAnimationFrame(() => {
        Promise.all([
          animate(leftGroupRef.current, "fadeIn"),
          animate(userDataButtonRef.current, "fadeIn"),
          animate(clearStateButtonRef.current, "fadeIn"),
          animate(stateDataButtonRef.current, "fadeIn"),
        ]);
      });
    }
  }, [showButtons]);

  return (
    <div
      id="pyne-debugger"
      className={`pyne-debugger-v2 ${isMinimized ? "minimized" : "expanded"}`}
      style={{ pointerEvents: "auto" }}
      ref={debuggerRef}
    >
      <div className="pyne-debugger-v2__toolbar-buttons-container">
        <div
          className={`pyne-debugger-v2__toolbar-button-group left ${showButtons ? "visible" : "invisible"}`}
          ref={leftGroupRef}
        >
          {showButtons && (
            <>
              <DebuggerToolbarButton
                onClick={() => manager.onGuideListButtonClicked()}
                active={manager.isGuideListModalVisible}
              >
                <ScrollTextIcon style={{ width: 16, height: 16 }} />
                <div>Guides</div>
                {manager.isGuideListModalVisible ? (
                  <ChevronDownIcon style={{ width: 16, height: 16 }} />
                ) : (
                  <ChevronUpIcon style={{ width: 16, height: 16 }} />
                )}
              </DebuggerToolbarButton>
              <DebuggerToolbarButton
                active={manager.isTourDetailsModalVisible}
                disabled={!manager.tourLifecycleManager.currentGuide}
                onClick={() => manager.onTourDetailsButtonClicked()}
              >
                <LandPlotIcon style={{ width: 16, height: 16 }} />
                <div>Current: {manager.currentGuide?.title ?? "No guide active"}</div>
              </DebuggerToolbarButton>
              <DebuggerToolbarButton
                active={manager.isChecklistListModalVisible}
                onClick={() => manager.toggleChecklistListModal()}
              >
                <ListIcon style={{ width: 16, height: 16 }} />
                <div>Checklists</div>
                {manager.isChecklistListModalVisible ? (
                  <ChevronDownIcon style={{ width: 16, height: 16 }} />
                ) : (
                  <ChevronUpIcon style={{ width: 16, height: 16 }} />
                )}
              </DebuggerToolbarButton>
            </>
          )}
        </div>
        <div className={`pyne-debugger-v2__toolbar-button-group right ${showButtons ? "visible" : "invisible"}`}>
          {showButtons && (
            <>
              <div ref={clearStateButtonRef}>
                <DebuggerToolbarButton onClick={() => manager.clearState()}>
                  <TrashIcon style={{ width: 16, height: 16 }} />
                  <div>Start over</div>
                </DebuggerToolbarButton>
              </div>
              {manager.isDevModeEnabled && (
                <div ref={stateDataButtonRef}>
                  <DebuggerToolbarButton onClick={() => manager.toggleStateDataModal()}>
                    <BoxIcon style={{ width: 16, height: 16 }} />
                    <div>State</div>
                  </DebuggerToolbarButton>
                </div>
              )}
              <div ref={userDataButtonRef}>
                <DebuggerToolbarButton onClick={() => manager.toggleUserDataModal()}>
                  <UserIcon style={{ width: 16, height: 16 }} />
                  <div>User data</div>
                </DebuggerToolbarButton>
              </div>
            </>
          )}
          <div className="pyne-debugger-v2__toolbar-controls">
            <DebuggerToolbarButton onClick={handleLogoClick}>
              <Logo style={{ width: 48, height: 16 }} />
            </DebuggerToolbarButton>
            {isMinimized ? (
              <DebuggerToolbarButton onClick={handleExpand}>
                <ExpandIcon style={{ width: 16, height: 16 }} />
              </DebuggerToolbarButton>
            ) : (
              <DebuggerToolbarButton onClick={handleMinimize}>
                <Minimize2Icon style={{ width: 16, height: 16 }} />
              </DebuggerToolbarButton>
            )}
          </div>
        </div>
      </div>
      <GuideListModal
        manager={manager}
        isVisible={manager.isGuideListModalVisible}
        onClose={() => manager.closeGuideList()}
      />
      <ChecklistListModal
        manager={manager}
        isVisible={manager.isChecklistListModalVisible}
        onClose={() => manager.closeChecklistListModal()}
      />
      <TourDetailsModal
        manager={manager}
        isVisible={manager.isTourDetailsModalVisible && !manager.isSelectorPickerActive}
        onClose={() => manager.closeTourDetails()}
        onBack={() => manager.openGuideList()}
        onTerminateTour={() => manager.terminateCurrentTour()}
        onStartTour={() => manager.startTour()}
        onStartTourFromStep={(stepId) => manager.startTourFromStep(stepId)}
      />
      <UserDataModal
        manager={manager}
        isVisible={manager.isUserDataModalVisible}
        onClose={() => manager.closeUserDataModal()}
      />
      <StateDataModal
        manager={manager}
        isVisible={manager.isStateDataModalVisible}
        onClose={() => manager.closeStateDataModal()}
      />
      {ReactDOM.createPortal(
        <div id="pyne-selector-picker">
          <Highlighter
            isSelecting={manager.isSelectorPickerActive}
            onPick={handlePick}
            onScreenshot={() => manager.requestScreenshotForCurrentlyPickingStep()}
          />
        </div>,
        document.body
      )}
      {(manager.isSelectorPickerActive || manager.isSelectorUpdateLoading) &&
        ReactDOM.createPortal(
          <div id="pyne-selector-picker-badge" style={{ pointerEvents: "auto" }}>
            <SelectorPickerBadge
              isLoading={manager.isSelectorUpdateLoading}
              onCancel={() => manager.disableSelectorPickerMode()}
            />
          </div>,
          document.body
        )}
      {manager.isSelectorUpdateSuccessToastVisible && (
        <Toast
          message="Selector updated successfully!"
          type="success"
          onClose={() => manager.hideSelectorUpdateSuccessToast()}
        />
      )}
      {manager.isSelectorUpdateFailedToastVisible && (
        <Toast
          message="Failed to update selector."
          type="error"
          onClose={() => manager.hideSelectorUpdateFailedToast()}
        />
      )}
    </div>
  );
});

interface DebuggerToolbarButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode;
  disabled?: boolean;
  active?: boolean;
}

const DebuggerToolbarButton = ({ children, disabled, active, ...props }: DebuggerToolbarButtonProps) => {
  return (
    <button
      className={`pyne-debugger-v2__toolbar-button ${!active && disabled ? "disabled" : ""} ${active ? "active" : ""}`}
      {...props}
      onClick={disabled && !active ? undefined : props.onClick}
    >
      {children}
    </button>
  );
};

interface ToastProps {
  message: string;
  type: "success" | "error";
  onClose: () => void;
}

const Toast: React.FC<ToastProps> = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 3000); // Auto-close after 3 seconds

    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div
      style={{
        position: "fixed",
        top: "20px",
        left: "50%",
        transform: "translateX(-50%)",
        backgroundColor: type === "success" ? "green" : "red",
        color: "white",
        padding: "4px 8px",
        borderRadius: "5px",
        zIndex: 100000,
        display: "flex",
        alignItems: "center",
        gap: "10px",
        fontSize: "12px",
      }}
    >
      {message}
    </div>
  );
};
